import { CommonModule } from "@angular/common";
import { Component, input, output, inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";

import { filter } from "rxjs";

import { ClientContact } from "@features/catalog/client/models";

import {
  ChipComponent,
  ChipVariant,
} from "@app/shared/components/chip/chip.component";
import { ContactModalFormData } from "@app/shared/components/contact-modal/contact-modal.component";
import { ContactModalService } from "@app/shared/components/contact-modal/contact-modal.service";

export type ContactActionType = "add" | "delete" | "edit";

export interface ContactActionEvent {
  action: ContactActionType;
  contact: ClientContact;
  index?: number;
}

@Component({
  selector: "app-contacts-list",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ChipComponent,
  ],
  templateUrl: "./contacts-list.component.html",
  styleUrl: "./contacts-list.component.scss",
})
export class ContactsListComponent {
  private readonly contactModalService = inject(ContactModalService);

  readonly title = input.required<string>();
  readonly contacts = input.required<ClientContact[]>();
  readonly addButtonText = input<string>("Add Contact");

  readonly contactAction = output<ContactActionEvent>();

  readonly displayedColumns: string[] = [
    "contactName",
    "contactEmail",
    "contactType",
    "contactPhone",
    "actions",
  ];

  onAddContact(): void {
    this.contactModalService
      .openAddContact()
      .pipe(filter((result) => result !== null && result !== undefined))
      .subscribe((formData: ContactModalFormData) => {
        const newContact: ClientContact = {
          ...formData,
          isPrimary: false,
        };
        this.contactAction.emit({
          action: "add",
          contact: newContact,
        });
      });
  }

  onDeleteContact(contact: ClientContact, index: number): void {
    this.contactAction.emit({
      action: "delete",
      contact,
      index,
    });
  }

  onEditContact(contact: ClientContact, index: number): void {
    const contactData: ContactModalFormData = {
      contactType: contact.contactType,
      contactName: contact.contactName,
      contactEmail: contact.contactEmail,
      contactPhone: contact.contactPhone ?? "",
    };

    this.contactModalService
      .openEditContact(contactData)
      .pipe(filter((result) => result !== null && result !== undefined))
      .subscribe((formData: ContactModalFormData) => {
        const updatedContact: ClientContact = {
          ...formData,
          isPrimary: contact.isPrimary,
        };

        this.contactAction.emit({
          action: "edit",
          contact: updatedContact,
          index,
        });
      });
  }

  getContactTypeVariant(contactType: string): ChipVariant {
    const variantMap: Record<string, ChipVariant> = {
      BILLING: "secondary-blue",
      BUSINESS: "orange",
      TECHNICAL: "primary-blue",
      SUPPORT: "green",
    };

    return variantMap[contactType.toUpperCase()] || "neutral";
  }
}
